/* The console's only door to the world: the server's JSON API on the same origin (the dev server proxies
   /api). No credential is ever held here; model keys live in the server's environment. */
import type { BedState, CallResult, DiffView, PanelView, RunMeta, RunScore, CaseResult, Timeline, TrialsView, WorldInfo, CallRecord } from './types.ts'

export class ApiError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function call<T>(path: string, init?: { method?: string; body?: unknown; signal?: AbortSignal }): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method: init?.method ?? 'GET',
      headers: init?.body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
      signal: init?.signal,
    })
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e
    throw new ApiError(0, 'The server is not answering. Start it with npm run console.')
  }
  const text = await res.text()
  let body: unknown = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = null
  }
  if (!res.ok) throw new ApiError(res.status, (body as { error?: string } | null)?.error ?? `${res.status} ${res.statusText}`)
  return body as T
}

export type PanelRequest = { clusters?: string[]; size?: number; seed?: string; split?: string; blind?: boolean }
export type RunRequest = { agent: string; model?: string; base_url?: string; effort?: string; clusters?: string[]; size?: number; seed?: string; split?: string }

export const api = {
  world: (signal?: AbortSignal) => call<WorldInfo>('/api/world', { signal }),
  panel: (body: PanelRequest) => call<PanelView>('/api/panels', { method: 'POST', body }),
  openBed: (body: { case_id: string; seed?: string; blind?: boolean }) => call<BedState>('/api/bedside', { method: 'POST', body }),
  bed: (id: string) => call<BedState>(`/api/bedside/${encodeURIComponent(id)}`),
  callTool: (id: string, tool: string, args: unknown) => call<{ result: CallResult; state: BedState }>(`/api/bedside/${encodeURIComponent(id)}/call`, { method: 'POST', body: { tool, args } }),
  endBed: (id: string) => call<{ debrief: Timeline }>(`/api/bedside/${encodeURIComponent(id)}/end`, { method: 'POST', body: {} }),
  debrief: (id: string) => call<{ debrief: Timeline }>(`/api/bedside/${encodeURIComponent(id)}/debrief`),
  runs: () => call<{ runs: RunMeta[] }>('/api/runs'),
  startRun: (body: RunRequest) => call<{ run: RunMeta }>('/api/runs', { method: 'POST', body }),
  run: (id: string) => call<{ run: RunMeta; results: CaseResult[]; score: RunScore | null }>(`/api/runs/${encodeURIComponent(id)}`),
  runCase: (id: string, c: string) => call<{ result: CaseResult | null; transcript: CallRecord[] }>(`/api/runs/${encodeURIComponent(id)}/cases/${encodeURIComponent(c)}`),
  replay: (id: string, c: string) => call<{ replay: Timeline }>(`/api/runs/${encodeURIComponent(id)}/cases/${encodeURIComponent(c)}/replay`),
  diff: (a: string, b: string) => call<DiffView>(`/api/diff?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`),
  trials: () => call<TrialsView>('/api/trials'),
  startTrials: (body: { ids?: string[]; size?: number }) => call<{ job: { id: string } }>('/api/trials', { method: 'POST', body }),
}

/** Follows an event stream until it ends; returns a stop function. */
export function follow(path: string, on: (event: string, data: unknown) => void, onEnd?: () => void): () => void {
  const es = new EventSource(path)
  const events = ['status', 'case', 'trial', 'done', 'failed']
  for (const e of events)
    es.addEventListener(e, (m) => {
      let data: unknown = null
      try {
        data = JSON.parse((m as MessageEvent<string>).data)
      } catch {
        data = null
      }
      on(e, data)
      if (e === 'done' || e === 'failed') {
        es.close()
        onEnd?.()
      }
    })
  es.onerror = () => {
    if (es.readyState === EventSource.CLOSED) onEnd?.()
  }
  return () => es.close()
}
